import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { TRADE_KEYS, type TradeKey } from '@/lib/schemas';

import { useCreateBusiness } from './admin-actions-api';
import type { createdBusinessSchema } from './admin-schemas';
import { tradeLabel } from './labels';
import type { z } from 'zod';

type Created = z.infer<typeof createdBusinessSchema>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * פתיחת עסק ע"י המפעיל: העסק + הבעלים. הבעלים מקבל סיסמה זמנית (מוצגת פעם
 * אחת, עם הודעה מוכנה לשליחה) ומחליף אותה בכניסה הראשונה.
 */
export function CreateBusinessDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (r: Created) => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const toast = useToast();
  const create = useCreateBusiness();
  const [values, setValues] = useState({
    businessName: '',
    trade: 'HVAC' as TradeKey,
    locale: 'he' as 'he' | 'en',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof values, string>>>({});
  const set = <K extends keyof typeof values>(k: K, v: (typeof values)[K]) =>
    setValues((s) => ({ ...s, [k]: v }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (values.businessName.trim().length < 2) next.businessName = t('create.errors.businessName');
    if (!values.ownerName.trim()) next.ownerName = t('create.errors.ownerName');
    if (!EMAIL_RE.test(values.ownerEmail.trim())) next.ownerEmail = t('create.errors.ownerEmail');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    create.mutate(
      {
        businessName: values.businessName.trim(),
        trade: values.trade,
        locale: values.locale,
        ownerName: values.ownerName.trim(),
        ownerEmail: values.ownerEmail.trim().toLowerCase(),
        ...(values.ownerPhone.trim() ? { ownerPhone: values.ownerPhone.trim() } : {}),
      },
      {
        onSuccess: onCreated,
        onError: (err) =>
          toast.error(t('create.failed'), err instanceof ApiError ? err.message : undefined),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="create-business-desc">
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription id="create-business-desc">{t('create.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate>
          <DialogBody>
            <Field
              label={t('create.businessName')}
              htmlFor="cb-name"
              error={errors.businessName}
              required
            >
              <Input
                id="cb-name"
                autoFocus
                autoComplete="off"
                maxLength={120}
                value={values.businessName}
                invalid={!!errors.businessName}
                onChange={(e) => set('businessName', e.target.value)}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('create.trade')} htmlFor="cb-trade">
                <Select value={values.trade} onValueChange={(v) => set('trade', v as TradeKey)}>
                  <SelectTrigger id="cb-trade">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRADE_KEYS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {tradeLabel(k)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t('create.locale')} htmlFor="cb-locale">
                <Select
                  value={values.locale}
                  onValueChange={(v) => set('locale', v as 'he' | 'en')}
                >
                  <SelectTrigger id="cb-locale">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="he">{t('lang.he')}</SelectItem>
                    <SelectItem value="en">{t('lang.en')}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field
              label={t('create.ownerName')}
              htmlFor="cb-owner"
              error={errors.ownerName}
              required
            >
              <Input
                id="cb-owner"
                autoComplete="off"
                maxLength={120}
                value={values.ownerName}
                invalid={!!errors.ownerName}
                onChange={(e) => set('ownerName', e.target.value)}
              />
            </Field>
            <Field
              label={t('create.ownerEmail')}
              htmlFor="cb-email"
              error={errors.ownerEmail}
              required
            >
              <Input
                id="cb-email"
                type="email"
                dir="ltr"
                autoComplete="off"
                maxLength={254}
                value={values.ownerEmail}
                invalid={!!errors.ownerEmail}
                onChange={(e) => set('ownerEmail', e.target.value)}
              />
            </Field>
            <Field
              label={t('create.ownerPhone')}
              htmlFor="cb-phone"
              hint={t('create.ownerPhoneHint')}
            >
              <Input
                id="cb-phone"
                type="tel"
                dir="ltr"
                autoComplete="off"
                maxLength={40}
                value={values.ownerPhone}
                onChange={(e) => set('ownerPhone', e.target.value)}
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t('common:actions.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" loading={create.isPending}>
              {t('create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
